import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Undo2,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { htmlToMarkdown, markdownToHtml } from '../lib/markdown.js';
import { cn } from '../lib/cn.js';

/**
 * The editor produktansvarlige actually write in.
 *
 * Markdown stays the storage format — agent drafts, version history and channel
 * adaptations all speak it — but nobody has to see the syntax
 * (dokumentasjon/07-utviklingsplan.md 2.1.3). Formatting is deliberately limited
 * to what survives the trip to a LinkedIn post, an article and a newsletter:
 * headings, emphasis, lists, quote and link. No fonts, no colours, no tables.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  readOnly = false,
  minHeight = 320,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: number;
  /** Put the caret in the editor on mount — used when a blocked move sends the
   * user here to write the missing draft. */
  autoFocus?: boolean;
}) {
  // What we last handed upwards. Lets us tell an outside change (a version
  // restored, an agent draft arriving) from the echo of our own typing.
  const emitted = useRef(value);

  const editor = useEditor(
    {
      editable: !readOnly,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
          codeBlock: false,
          horizontalRule: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto'],
        }),
        Placeholder.configure({ placeholder: placeholder ?? 'Skriv innholdet her …' }),
      ],
      content: markdownToHtml(value),
      editorProps: {
        attributes: {
          class: 'nova-prose focus:outline-none',
          spellcheck: 'true',
          lang: 'nb',
        },
      },
      onUpdate: ({ editor: instance }) => {
        const markdown = htmlToMarkdown(instance.getHTML());
        emitted.current = markdown;
        onChange(markdown);
      },
    },
    [readOnly],
  );

  // Reset only when the value changed somewhere other than in here — replacing
  // the document on every keystroke would cost the caret its place.
  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
  }, [editor, value]);

  // Drop the caret into the draft when asked (a blocked move sent us here).
  useEffect(() => {
    if (editor && autoFocus && !readOnly) editor.commands.focus('end');
  }, [editor, autoFocus, readOnly]);

  if (!editor) {
    return (
      <div
        className="border-hairline bg-base/40 rounded-lg border"
        style={{ minHeight: minHeight + 40 }}
      />
    );
  }

  return (
    <div className="border-hairline bg-base/40 rounded-lg border">
      {!readOnly && <Toolbar editor={editor} />}
      <div style={{ minHeight }} className="p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const linkPrompt = () => {
    const current = editor.getAttributes('link').href as string | undefined;
    const href = window.prompt('Lenkeadresse', current ?? 'https://');
    if (href === null) return;
    if (href.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
  };

  return (
    <div className="border-hairline flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5">
      <ToolButton
        label="Fet"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={13} />
      </ToolButton>
      <ToolButton
        label="Kursiv"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={13} />
      </ToolButton>

      <Divider />

      <ToolButton
        label="Overskrift"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 size={13} />
      </ToolButton>
      <ToolButton
        label="Underoverskrift"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 size={13} />
      </ToolButton>

      <Divider />

      <ToolButton
        label="Punktliste"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={13} />
      </ToolButton>
      <ToolButton
        label="Nummerert liste"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={13} />
      </ToolButton>
      <ToolButton
        label="Sitat"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote size={13} />
      </ToolButton>

      <Divider />

      <ToolButton label="Lenke" active={editor.isActive('link')} onClick={linkPrompt}>
        <Link2 size={13} />
      </ToolButton>

      <ToolButton
        label="Angre"
        active={false}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 size={13} />
      </ToolButton>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="bg-hairline mx-1 h-4 w-px" />;
}

function ToolButton({
  label,
  active,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'rounded-md p-1.5 transition disabled:opacity-30',
        active ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink hover:bg-raised',
      )}
    >
      {children}
    </button>
  );
}

/** Shared markdown renderer for read-only surfaces: versions, channel versions. */
export function MarkdownView({ markdown }: { markdown: string }) {
  return (
    <div className="text-ink-soft space-y-3 text-sm leading-relaxed">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="font-display text-ink text-lg font-medium">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="font-display text-ink pt-2 text-base font-medium">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="font-display text-ink pt-1 text-sm font-medium">{children}</h3>
          ),
          p: ({ children }) => <p>{children}</p>,
          strong: ({ children }) => <strong className="text-ink font-semibold">{children}</strong>,
          em: ({ children }) => <em className="text-ink-soft/90 italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
          blockquote: ({ children }) => (
            <blockquote className="border-accent/40 text-ink-soft/90 border-l-2 pl-3 italic">
              {children}
            </blockquote>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-accent underline underline-offset-2"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="bg-raised text-ink rounded px-1 py-0.5 font-mono text-[12px]">
              {children}
            </code>
          ),
          hr: () => <hr className="border-hairline" />,
        }}
      >
        {markdown}
      </Markdown>
    </div>
  );
}
